import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import documentTypeService from '../../service/newDocumentType.service';
import documentPiorityService from '../../service/documentPiority.service';
import DocumentPiorityFields from './DocumentPiorityFields';
import DocumentPiorityReports from './DocumentPiorityReports';

const defaultValues = {
  priorityId: null,
  documentTypeId: null,
  name: '',
  color: '#FFFFFF',
  visOrder: '',
  active: true,
  defaultValue: false,
};

const DocumentPiorityPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonVisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(true);
  const [initValues, setInitValues] = useState(defaultValues);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    documentTypeService.allDocumentTypes().then((data) => {
      setDocumentTypes(data);
      setDocumentTypeOptions(generateAutocompleteItems(data, 'name', 'documentTypeId'));
    });
  }, []);

  useEffect(() => {
    setCanEdit(getCanEdit(getMenuSubMenuId('documentPiority')));
  }, [menus]);

  useEffect(() => {
    const hasSelection = Object.keys(selectedValues).length > 0;
    const isDefaultSelection = isEqual(defaultValues, selectedValues);

    setOperatorsDisabled(!hasSelection || isDefaultSelection);
    setInitValues(hasSelection ? { ...defaultValues, ...selectedValues } : defaultValues);
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    documentTypeId: Yup.number().nullable().required(),
    name: Yup.string().required(),
    color: Yup.string()
      .nullable()
      .matches(/^$|^#(?:[0-9a-fA-F]{3}){1,2}$/)
      .max(7),
    visOrder: Yup.number().required(),
    defaultValue: Yup.boolean(),
    active: Yup.boolean(),
  });

  const finishSave = (data) => {
    setSelectedValues(data);
    setRowSelectionModel([data.priorityId]);
    setRefetchTable(true);
    setEditButtonVisible(true);
    setAddField(true);
    setViewField(true);
  };

  const handleSubmit = (values) => {
    const request = editButtonVisible
      ? documentPiorityService.createItem(cleanValues(values))
      : documentPiorityService.updateItem(cleanValues(values));

    request.then((data) => {
      addNotification({
        content: editButtonVisible ? 'Sikeres mentés!' : 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      finishSave(data);
    });
  };

  return (
    <FormBase
      name="DocumentPiority"
      leftPanelContent={
        <div className="h-full overflow-auto custom-scrollbar">
          <FormHeaderUI
            addClick={() => {
              setSelectedValues(defaultValues);
              setViewField(false);
              setAddField(false);
            }}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonVisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setSelectedValues({ ...selectedValues, priorityId: null });
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />
          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ resetForm }) => (
              <Form>
                <DocumentPiorityFields viewField={viewField} documentTypeOptions={documentTypeOptions} />
                <FormButtonPanelUI
                  onCancelClick={() => {
                    setViewField(true);
                    setEditButtonVisible(true);
                    setAddField(true);
                    resetForm();
                  }}
                  disabled={editButtonVisible && addField}
                />
              </Form>
            )}
          </Formik>
        </div>
      }
      rightPanelContent={
        <DocumentPiorityReports
          refetch={{ refetchTable, setRefetchTable }}
          values={{ selectedValues, setSelectedValues }}
          viewField={viewField}
          operatorsDisabled={operatorsDisabled}
          rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          documentTypes={documentTypes}
        />
      }
    />
  );
};

export default DocumentPiorityPage;
